import ContentBlock from "@/components/ContentBlock";
import ExpandableBlock from "@/components/ContentBlock/variants/ExpandableBlock";
import {
  DEFAULT_USE_IN_VIEW_OPTIONS,
  ElementType,
} from "@/constants/tagular/main";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import { cn } from "@/lib/utils";
import parse from "html-react-parser";
import { useEffect } from "react";
import { useInView } from "react-intersection-observer";

export interface AuthorStatementTestimonial {
  textRaw: string;
  author: string;
}
export interface LearnerTestimonialsProps {
  testimonials: string | AuthorStatementTestimonial[];
  showTitle?: boolean;
  contentClassName?: string;
  containerBlockClassName?: string;
  trackEvents?: boolean;
  title?: string;
  titleClassName?: string;
}

export default function LearnerTestimonials({
  title = "Learner Testimonials",
  testimonials,
  showTitle = true,
  contentClassName = "",
  trackEvents = false,
  containerBlockClassName = "",
  titleClassName = "",
}: LearnerTestimonialsProps) {
  const { elementViewed } = useElementEvents({
    webElement: {
      elementType: ElementType.Component,
      text: "Learner Testimonials",
      htmlId: "testimonials",
      name: "testimonials",
      location: "degree-detail-page",
      position: "1",
    },
  });

  const { ref: contentRef, inView } = useInView(DEFAULT_USE_IN_VIEW_OPTIONS);

  useEffect(() => {
    if (trackEvents && inView) {
      elementViewed();
    }
  }, [trackEvents, inView, elementViewed]);

  function styleTestimonials(html: string) {
    const paragraphs = html.match(/<p\b[^>]*>(.*?)<\/p>/g);
    if (paragraphs) {
      const lastParagraph = paragraphs[paragraphs.length - 1];
      const modifiedLastParagraph = lastParagraph.replace(
        "<p",
        '<p class="mb-0"',
      );
      html = html.replace(lastParagraph, modifiedLastParagraph);
    }

    return html
      .replace(/<ul>/g, '<ul class="list-disc pl-4 text-base">')
      .replace(/<ol>/g, '<ol class="list-decimal pl-4 text-base">')
      .replace(/<li>/g, '<li class="ml-4 list-outside">')
      .replace(/<a /g, '<a class="text-info-500 hover:underline" ');
  }

  return (
    <div ref={contentRef}>
      {showTitle && (
        <h2 className={cn("mt-0 md:text-3xl", titleClassName)}>{title}</h2>
      )}
      <div className="gap-6 flex flex-col">
        {typeof testimonials === "string" && (
          <ContentBlock
            variant="quoted"
            imageurl={""}
            imagetitle={""}
            className={containerBlockClassName}
          >
            <div className="md:text-lg mb-0" style={{ lineHeight: "1.925rem" }}>
              <ExpandableBlock
                maxHeight={180}
                btnClassName="text-info-500 text-[16px] font-normal mt-4 no-underline"
              >
                {parse(styleTestimonials(testimonials))}
              </ExpandableBlock>
            </div>
          </ContentBlock>
        )}

        {Array.isArray(testimonials) &&
          testimonials.map((testimonial, index) => (
            <ContentBlock
              key={index}
              variant="quoted"
              imageurl={""}
              imagetitle={""}
              className={containerBlockClassName}
            >
              <div>
                <p className="md:text-lg" style={{ lineHeight: "1.925rem" }}>
                  <ExpandableBlock
                    maxHeight={92}
                    showMoreLabel="More"
                    btnClassName="text-info-500 text-[16px] font-normal mt-2 no-underline"
                    contentClassName={contentClassName}
                  >
                    {parse(testimonial.textRaw)}
                  </ExpandableBlock>
                </p>
                <p className="font-bold md:text-lg mb-0">
                  {testimonial.author}
                </p>
              </div>
            </ContentBlock>
          ))}
      </div>
    </div>
  );
}
